import { CardBrand } from '@stripe/stripe-js';

import {
  BillingInfoExists as BillingInfoExistsDto,
  BillingInfo as BillingInfoDto,
  ResponseListPaymentMethodsV1 as PaymentMethodApiListDto,
  PaymentMethodsList as PaymentMethodApiListDtoV3,
  UpdatePaymentMethodRequest as PaymentMethodUpdateDto,
  LineItem as LineItemDto,
  EstimateTransactionWithWarehouseShippingRequest as TaxesEstimationRequestDto,
  TransactionEstimate as TaxesEstimationResponseDto,
  InvoicesList as InvoicesListDto,
  PaymentMethod,
  SetupPaymentRequest,
  PaymentMethodType,
  CardPaymentMethodPaymentMethodTypeEnum,
  GetPaymentTermsResult as PaymentTermsResultDto,
  UpdateCustomerPaymentTermsRequest as UpdateCustomerPaymentTermsRequestDto,
  CollectionMethod,
  AmendLicenseCountRequest,
  LicenseCountAmendment,
  CustomerHasPendingSubscriptionChange as CustomerHasPendingSubscriptionChangeDto,
} from '@electricjs/core_entity-client';

import { Address } from '@electricjs/arc';

import { apiSlice, TagTypes } from '@/redux/slices/apiSlice';

import { CatalogCartEntry } from '@/types/storefront';
import {
  BillingInfo,
  PaymentMethodV2,
  PaymentMethodV3,
  CardPaymentMethodV3,
  AchPaymentMethodV3,
  SaveBillingInfoArgs,
  TaxesEstimationArgs,
  ManagePaymentMethodArgs,
  ListOrganizationInvoicesV2Response,
  ListOrganizationInvoicesV2Args,
  EstimatedTaxes,
  PaymentTerms,
  SetOrganizationDefaultPaymentMethodByStripeIdArgs,
} from '@/types/billing';

import { getPaymentInterestPercentage } from '@/components/Storefront/utils/helpers';
import { toNumber } from '@/helpers/toNumber';

// Defining enum to be used on the frontend (backend ended up not having one)
export enum PaymentType {
  InFull = 'in_full',
  InInstallments = 'in_installments',
}

/** Format the price to be sent to billing endpoints.
 *  The billing endpoints expect it to be a `string` representing the price with 2 decimal points.
 */
const formatPriceToBillingAPI = (value: number): string => value.toFixed(2);

/** Prepare line items to be sent to the billing endpoints.
 *  The warranties are added as new line items.
 */
const prepareLineItems = (
  productCart: CatalogCartEntry[],
  paymentType: PaymentType
): LineItemDto[] => {
  const orderLines: LineItemDto[] = [];

  productCart.forEach(entry => {
    orderLines.push({
      unit_price: formatPriceToBillingAPI(entry.item.price ?? 0),
      quantity: entry.quantity,
      sku: entry.item.vendorSKU ?? '',
      tax_code: entry.item.taxCode,
      name: entry.item.name,
      is_paid_over_time: paymentType === PaymentType.InInstallments,
    });

    // Add the extended warranty as line item when it exists
    if (
      entry.selectedWarranty?.id &&
      entry.selectedWarranty.id !==
        entry.item.availableWarranties?.defaultWarrantyId
    ) {
      orderLines.push({
        unit_price: formatPriceToBillingAPI(entry.selectedWarranty.price ?? 0),
        quantity: entry.quantity,
        sku: entry.selectedWarranty.vendorSKU ?? '',
        tax_code: entry.selectedWarranty.taxCode,
        name: entry.selectedWarranty.name,
        is_paid_over_time: paymentType === PaymentType.InInstallments,
      });
    }
  });

  return orderLines;
};

const prepareTaxesEstimationPayload = ({
  shippingAddress,
  currency,
  catalogShippingCost,
  selectedProducts,
  selectedPaymentType,
  selectedInstallmentsCount,
  warehouseShippingCost,
  warehouseShippingMethod,
}: TaxesEstimationArgs): TaxesEstimationRequestDto => {
  return {
    shipping_info: {
      address: {
        line1: shippingAddress.streetAddress1,
        line2: shippingAddress.streetAddress2 ?? '',
        city: shippingAddress.city,
        region: shippingAddress.state,
        country_code: shippingAddress.country,
        postal_code: shippingAddress.zip,
      },
      amount: formatPriceToBillingAPI(catalogShippingCost),
      ship_to_name: '',
    },
    currency,
    line_items: prepareLineItems(selectedProducts, selectedPaymentType),
    pay_over_time_settings:
      selectedPaymentType === PaymentType.InInstallments &&
      selectedInstallmentsCount
        ? {
            finance_charge_basis_points:
              getPaymentInterestPercentage(selectedInstallmentsCount) * 100, // multiplying by 100 (e.g. 6.25% -> 625)
            interval_count: selectedInstallmentsCount,
            interval_length_months: 1,
          }
        : undefined,
    warehouse_shipping_info:
      warehouseShippingCost !== undefined && warehouseShippingMethod
        ? {
            warehouse_shipping_fee: formatPriceToBillingAPI(
              warehouseShippingCost
            ),
            warehouse_shipping_method: warehouseShippingMethod,
          }
        : undefined,
  };
};

export const billingApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    //
    // Organization invoices v2
    //
    getOrganizationInvoicesV2: builder.query<
      ListOrganizationInvoicesV2Response,
      ListOrganizationInvoicesV2Args
    >({
      query: arg => {
        const { type, status } = arg;
        return {
          url: `/v2/organizations/${arg.organizationId}/invoices`,
          params: {
            status,
            type,
          },
        };
      },
      transformResponse: (response: InvoicesListDto) => {
        const organizationInvoices =
          response.invoices.map(invoice => {
            return {
              invoiceId: invoice.invoice_id,
              organizationId: invoice.organization_id,
              invoiceNumber: invoice.invoice_number,
              transactionType: invoice.transaction_type,
              sourceType: invoice.source_type,
              status: invoice.status,
              isOverdue: invoice.is_overdue,
              description: invoice.description,
              hostedInvoiceUrl: invoice.hosted_invoice_url,
              pdfUrl: invoice.pdf_url,
              amountPaid: toNumber(invoice.amount_paid),
              amountTotal: toNumber(invoice.amount_total),
              userId: invoice.user_id,
              orderId: invoice.order_id,
              dueDate: invoice.due_date,
              createdAt: invoice.created_at,
              paidAt: invoice.paid_at,
            };
          }) ?? [];
        return {
          invoices: organizationInvoices,
        };
      },
    }),

    ///
    /// Billing v2 (based on Stripe)
    ///

    checkBillingInfoExists: builder.query<boolean, string>({
      query: organizationId => ({
        url: `/v2/organizations/${organizationId}/billing/info/exists`,
      }),
      transformResponse: (response: BillingInfoExistsDto) =>
        response.billing_info_exists,
      providesTags: [TagTypes.BillingInfo],
    }),
    getBillingInfo: builder.query<BillingInfo, string>({
      query: organizationId => ({
        url: `/v2/organizations/${organizationId}/billing/info`,
      }),
      transformResponse: (response: BillingInfoDto) => {
        const { city, country_code, line1, line2, postal_code, region } =
          response.billing_address;

        const address: Address = {
          city,
          streetAddress1: line1,
          streetAddress2: line2,
          state: region,
          country: country_code, // the ISO code will go as the country name, because the endpoint doesn't return the country's name
          zip: postal_code,
        };

        return {
          email: response.billing_email,
          address: address,
        };
      },
      providesTags: [TagTypes.BillingInfo],
    }),
    saveBillingInfo: builder.mutation<void, SaveBillingInfoArgs>({
      query: ({ organizationId, billingInfo }) => {
        const body: BillingInfoDto = {
          organization_id: organizationId,
          billing_email: billingInfo.email,
          billing_address: {
            line1: billingInfo.address.streetAddress1,
            line2: billingInfo.address.streetAddress2 ?? '',
            city: billingInfo.address.city,
            region: billingInfo.address.state,
            country_code: billingInfo.address.country,
            postal_code: billingInfo.address.zip,
          },
        };

        return {
          url: `/v2/organizations/${organizationId}/billing/info`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: [TagTypes.BillingInfo],
    }),
    getUserClientSecret: builder.mutation<string, string>({
      query: organizationId => ({
        url: `/v2/organizations/${organizationId}/billing/setup_payment`,
        method: 'POST',
      }),
      transformResponse: (response: { client_secret: string }) => {
        return response.client_secret;
      },
    }),
    getStripeClientSecret: builder.mutation<
      string,
      {
        organizationId: string;
        paymentMethodCategory: SetupPaymentRequest['payment_method_category'];
      }
    >({
      query: ({ organizationId, paymentMethodCategory }) => ({
        url: `/v3/organizations/${organizationId}/billing/setup_payment`,
        method: 'POST',
        body: {
          payment_method_category: paymentMethodCategory,
        },
      }),
      transformResponse: (response: { client_secret: string }) => {
        return response.client_secret;
      },
    }),
    getPaymentMethods: builder.query<PaymentMethodV2[], string>({
      query: organizationId =>
        `/v2/organizations/${organizationId}/billing/payment_methods`,
      transformResponse: (response: PaymentMethodApiListDto) =>
        response.card_payment_methods.map(paymentMethod => ({
          nickname: paymentMethod.nickname,
          id: paymentMethod.payment_method_id,
          brand: paymentMethod.brand as CardBrand,
          ownerUserId: paymentMethod.owner_user_id,
          displayBrand: paymentMethod.display_brand,
          expirationMonth: paymentMethod.expiration_month,
          expirationYear: paymentMethod.expiration_year,
          lastFourDigits: paymentMethod.last_four_digits,
          address: {
            streetAddress1: paymentMethod.address.line1 ?? '',
            streetAddress2: paymentMethod.address.line2,
            city: paymentMethod.address.city ?? '',
            state: paymentMethod.address.region ?? '',
            country: paymentMethod.address.country_code ?? '', // the ISO code will go as the country name, because the endpoint doesn't return the country's name
            zip: paymentMethod.address.postal_code ?? '',
          },
          isDefault: paymentMethod.is_user_default,
        })),
      providesTags: [TagTypes.PaymentMethods],
    }),
    getOrganizationPaymentMethods: builder.query<PaymentMethodV3[], string>({
      query: organizationId =>
        `/v3/organizations/${organizationId}/billing/payment_methods`,
      transformResponse: (response: PaymentMethodApiListDtoV3) => {
        // Type guards to safely narrow down the payment method types
        const isCardPaymentMethod = (
          method: PaymentMethod
        ): method is PaymentMethod & {
          payment_method_type: CardPaymentMethodPaymentMethodTypeEnum;
        } => {
          return (
            method.payment_method_type ===
            CardPaymentMethodPaymentMethodTypeEnum.Card
          );
        };

        return response.payment_methods.map(method => {
          const basePaymentMethod: Omit<
            PaymentMethodV3,
            'paymentMethodType' | 'lastFourDigits'
          > = {
            id: method.payment_method_id,
            address: {
              streetAddress1: method.address.line1 ?? '',
              streetAddress2: method.address.line2 ?? '',
              city: method.address.city ?? '',
              state: method.address.region ?? '',
              country: method.address.country_code ?? '',
              zip: method.address.postal_code ?? '',
            },
            ownerId: method.owner_id,
            category: method.category,
            isDefault: method.is_default || false,
            nickname: method.nickname,
          };
          if (isCardPaymentMethod(method)) {
            // Handle card payment method
            const cardPaymentMethod: CardPaymentMethodV3 = {
              ...basePaymentMethod,
              paymentMethodType: method.payment_method_type,
              brand: method.brand as CardBrand,
              displayBrand: method.display_brand,
              expirationMonth: method.expiration_month,
              expirationYear: method.expiration_year,
              lastFourDigits: method.last_four_digits,
            };

            return cardPaymentMethod;
          } else {
            // Handle ACH payment method
            const achPaymentMethod: AchPaymentMethodV3 = {
              ...basePaymentMethod,
              paymentMethodType: method.payment_method_type,
              bankName: method.bank_name,
              accountHolderType: method.account_holder_type,
              accountType: method.account_type,
              lastFourDigits: method.last_four_digits,
            };

            return achPaymentMethod;
          }
        });
      },
      providesTags: [TagTypes.OrganizationPaymentMethods],
    }),
    setDefaultPaymentMethod: builder.mutation<void, ManagePaymentMethodArgs>({
      query: ({ organizationId, paymentMethodId }) => {
        return {
          url: `/v2/organizations/${organizationId}/billing/payment_methods/default`,
          method: 'POST',
          body: {
            payment_method_id: paymentMethodId,
          },
        };
      },
      invalidatesTags: [TagTypes.PaymentMethods],
    }),
    setOrganizationDefaultPaymentMethod: builder.mutation<
      void,
      ManagePaymentMethodArgs
    >({
      query: ({ organizationId, paymentMethodId }) => {
        return {
          url: `/v3/organizations/${organizationId}/billing/payment_methods/default`,
          method: 'POST',
          body: {
            payment_method_id: paymentMethodId,
          },
        };
      },
      invalidatesTags: [TagTypes.OrganizationPaymentMethods],
    }),
    setOrganizationDefaultPaymentMethodByStripeId: builder.mutation<
      void,
      SetOrganizationDefaultPaymentMethodByStripeIdArgs
    >({
      query: ({ organizationId, stripePaymentMethodId }) => {
        return {
          url: `/v3/organizations/${organizationId}/billing/payment_methods/default/stripe_id`,
          method: 'POST',
          body: {
            stripe_payment_method_id: stripePaymentMethodId,
          },
        };
      },
      invalidatesTags: [TagTypes.OrganizationPaymentMethods],
    }),
    editPaymentMethod: builder.mutation<
      void,
      PaymentMethodV2 & { organizationId: string }
    >({
      query: paymentMethod => {
        const { organizationId, id } = paymentMethod;
        const body: PaymentMethodUpdateDto = {
          nickname: paymentMethod.nickname,
          expiration: {
            month: paymentMethod.expirationMonth,
            year: paymentMethod.expirationYear,
          },
          address: {
            line1: paymentMethod.address.streetAddress1,
            line2: paymentMethod.address.streetAddress2 ?? '',
            city: paymentMethod.address.city ?? '',
            country_code: paymentMethod.address.country,
            postal_code: paymentMethod.address.zip ?? '',
            region: paymentMethod.address.state ?? '',
          },
        };
        return {
          url: `/v2/organizations/${organizationId}/billing/payment_methods/${id}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [TagTypes.PaymentMethods],
    }),
    deletePaymentMethod: builder.mutation<void, ManagePaymentMethodArgs>({
      query: ({ organizationId, paymentMethodId }) => {
        return {
          url: `/v2/organizations/${organizationId}/billing/payment_methods/${paymentMethodId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [TagTypes.PaymentMethods],
    }),
    editOrganizationPaymentMethod: builder.mutation<
      void,
      PaymentMethodV3 & { organizationId: string }
    >({
      query: paymentMethod => {
        const { organizationId, id: paymentMethodId } = paymentMethod;
        const body: PaymentMethodUpdateDto = {
          ...(paymentMethod.nickname && { nickname: paymentMethod.nickname }),
          ...(paymentMethod.paymentMethodType === PaymentMethodType.Card &&
            paymentMethod.expirationMonth &&
            paymentMethod.expirationYear && {
              expiration: {
                month: paymentMethod.expirationMonth,
                year: paymentMethod.expirationYear,
              },
            }),
          ...(paymentMethod.address && {
            address: {
              line1: paymentMethod.address.streetAddress1,
              line2: paymentMethod.address.streetAddress2 ?? '',
              city: paymentMethod.address.city ?? '',
              country_code: paymentMethod.address.country,
              postal_code: paymentMethod.address.zip ?? '',
              region: paymentMethod.address.state ?? '',
            },
          }),
        };
        return {
          url: `/v3/organizations/${organizationId}/billing/payment_methods/${paymentMethodId}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [TagTypes.OrganizationPaymentMethods],
    }),
    deleteOrganizationPaymentMethod: builder.mutation<
      void,
      ManagePaymentMethodArgs
    >({
      query: ({ organizationId, paymentMethodId }) => {
        return {
          url: `/v3/organizations/${organizationId}/billing/payment_methods/${paymentMethodId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [TagTypes.OrganizationPaymentMethods],
    }),
    estimateTaxes: builder.mutation<EstimatedTaxes, TaxesEstimationArgs>({
      query: args => {
        return {
          url: `/v2/organizations/${args.organizationId}/billing/transaction/estimate`,
          method: 'POST',
          body: prepareTaxesEstimationPayload(args),
        };
      },
      transformResponse: (response: TaxesEstimationResponseDto) => ({
        totalTaxes: toNumber(response.total_tax_estimate),
        todayTax: toNumber(response.initial_payment.total_tax_estimate),
        futureMonthlyTax: response.future_payments?.[0].total_tax_estimate
          ? toNumber(response.future_payments?.[0].total_tax_estimate)
          : undefined,
      }),
    }),
    getOrganizationPaymentTerms: builder.query<
      PaymentTerms | undefined,
      string
    >({
      query: organizationId =>
        `/v3/organizations/${organizationId}/billing/payment_terms`,
      transformResponse: (response: PaymentTermsResultDto) => {
        if (!response.payment_terms) {
          return undefined;
        }

        const paymentTerms = response.payment_terms;
        return {
          collectionMethod: paymentTerms.collection_method,
          daysUntilDue:
            paymentTerms.collection_method === CollectionMethod.SendInvoice
              ? paymentTerms.days_until_due
              : undefined,
        };
      },
      providesTags: [TagTypes.OrganizationPaymentTerms],
    }),
    updateOrganizationPaymentTerms: builder.mutation<
      void,
      PaymentTerms & { organizationId: string }
    >({
      query: ({ organizationId, collectionMethod, daysUntilDue }) => {
        const body: UpdateCustomerPaymentTermsRequestDto = {
          collection_method: collectionMethod,
          ...(daysUntilDue && { days_until_due: daysUntilDue }),
        };

        return {
          url: `/v3/organizations/${organizationId}/billing/payment_terms`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: [TagTypes.OrganizationPaymentTerms],
    }),
    amendLicenseCount: builder.mutation<
      LicenseCountAmendment,
      { organizationId: string; productId: string; newLicenseCount: number }
    >({
      query: ({ organizationId, productId, newLicenseCount }) => ({
        url: `/v3/organizations/${organizationId}/billing/subscription/license-count`,
        method: 'POST',
        body: {
          product_id: productId,
          new_license_count: newLicenseCount,
        } satisfies AmendLicenseCountRequest,
      }),
      invalidatesTags: [TagTypes.OrganizationPackage, TagTypes.Organization],
    }),
    checkCustomerHasPendingSubscriptionChange: builder.query<boolean, string>({
      query: organizationId => ({
        url: `/v3/organizations/${organizationId}/billing/subscription/pending-changes`,
      }),
      transformResponse: (response: CustomerHasPendingSubscriptionChangeDto) =>
        response.has_pending_subscription_change,
      providesTags: [TagTypes.BillingInfo],
    }),
  }),
});

// export auto-generated hooks
export const {
  useLazyGetOrganizationInvoicesV2Query,
  useGetOrganizationInvoicesV2Query,
  useCheckBillingInfoExistsQuery,
  useSaveBillingInfoMutation,
  useGetUserClientSecretMutation,
  useGetStripeClientSecretMutation,
  useGetPaymentMethodsQuery,
  useGetOrganizationPaymentMethodsQuery,
  useSetDefaultPaymentMethodMutation,
  useSetOrganizationDefaultPaymentMethodMutation,
  useEditPaymentMethodMutation,
  useEditOrganizationPaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useDeleteOrganizationPaymentMethodMutation,
  useGetBillingInfoQuery,
  useEstimateTaxesMutation,
  useGetOrganizationPaymentTermsQuery,
  useUpdateOrganizationPaymentTermsMutation,
  useSetOrganizationDefaultPaymentMethodByStripeIdMutation,
  useAmendLicenseCountMutation,
  useCheckCustomerHasPendingSubscriptionChangeQuery,
} = billingApiSlice;
